import { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "+41779954435";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace("+", "")}?text=${encodeURIComponent("Hello, I'd like to request access to DALFA.")}`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function RequestAccessPage({ onBack }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post("/api/auth/request-access", form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="dalfa-auth-page" data-testid="request-access-page">
        <div className="dalfa-auth-card">
          <div className="dalfa-auth-logo dalfa-success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <h1 className="dalfa-auth-title">Request Submitted</h1>
          <p className="dalfa-auth-subtitle">Your request has been received. We will contact you shortly.</p>
          <div className="dalfa-auth-actions">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dalfa-whatsapp-btn" data-testid="request-whatsapp-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .612.612l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.327 0-4.47-.774-6.197-2.079l-.433-.327-2.65.889.889-2.65-.327-.433A9.96 9.96 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
              Contact on WhatsApp
            </a>
            <button onClick={onBack} className="dalfa-auth-link-btn" data-testid="request-back-btn">Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dalfa-auth-page" data-testid="request-access-page">
      <div className="dalfa-auth-card dalfa-auth-card-wide">
        <button className="dalfa-back-btn" onClick={onBack} data-testid="request-back-btn-top">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>

        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_668c4879-79ca-423e-b1cc-dd12ca40e1a2/artifacts/j1j0s0hk_1937.png"
          alt="DALPHA PRIME 2.0"
          className="dalfa-auth-logo-img"
        />
        <h1 className="dalfa-auth-title">Request Access</h1>
        <p className="dalfa-auth-subtitle">Tell us about yourself and we'll get back to you.</p>

        <form onSubmit={handleSubmit} className="dalfa-auth-form" data-testid="request-access-form">
          {error && <div className="dalfa-auth-error" data-testid="request-error">{error}</div>}
          <div className="dalfa-auth-field">
            <label htmlFor="name">Name *</label>
            <input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required data-testid="request-name-input" />
          </div>
          <div className="dalfa-auth-field">
            <label htmlFor="email">Email *</label>
            <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" required data-testid="request-email-input" />
          </div>
          <div className="dalfa-auth-row">
            <div className="dalfa-auth-field">
              <label htmlFor="company">Company / Store</label>
              <input id="company" name="company" value={form.company} onChange={handleChange} placeholder="Your store name" data-testid="request-company-input" />
            </div>
            <div className="dalfa-auth-field">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" value={form.website} onChange={handleChange} placeholder="https://..." data-testid="request-website-input" />
            </div>
          </div>
          <div className="dalfa-auth-field">
            <label htmlFor="message">Message</label>
            <textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your needs..." rows={3} data-testid="request-message-input" />
          </div>
          <button type="submit" className="dalfa-auth-submit" disabled={loading} data-testid="request-submit-btn">
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </form>

        <div className="dalfa-auth-divider"><span>or</span></div>

        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dalfa-whatsapp-btn dalfa-whatsapp-btn-full" data-testid="request-whatsapp-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .612.612l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.327 0-4.47-.774-6.197-2.079l-.433-.327-2.65.889.889-2.65-.327-.433A9.96 9.96 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
          Contact via WhatsApp
        </a>
      </div>
    </div>
  );
}
